import { Component, Input } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { SiteDashboardService } from '../../site-dashboard.service';
import * as moment from 'moment';
import { AngularCsv } from 'angular-csv-ext/dist/Angular-csv';
import { DeviceService } from '../../device/device.service';
import { DeviceFactory } from '../../device/device-factory/device-factory';
import { CommonService } from 'src/app/shared/services/common.service';
import { SitesService } from 'src/app/sites/sites.service';

@Component({
	selector: 'app-monthly-daily-truck-usage-chart',
	templateUrl: './monthly-daily-truck-usage-chart.component.html',
	styleUrls: ['./monthly-daily-truck-usage-chart.component.css']
})
export class MonthlyDailyTruckUsageChartComponent {
	@Input() device: any;
	@Input() studyInfo: any = {}
	@Input() currentSite: any = {};

	siteId: number = 0;
	utilizationSettings: any = {};
	totalWorkingDays: number = 0;
	yearList: any[] = [];
	selectedYear: number = new Date().getFullYear();
	deviceInstallationDate = null;
	noData = false;

	selectedPeriodType = 'monthly';
	selectedGraphTypes = 'total';
	selectedCharts = 'all';

	chartsData = {
		total_inuse_hours: {},
		total_idle_hours: {},
		total_inuse_ah: {},
		total_inuse_kwh: {},
		total_travel_lift_time: {},
		average_inuse_hours: {},
		average_idle_hours: {},
		average_inuse_ah: {},
		average_inuse_kwh: {},
		average_travel_lift_time: {},
	};

	monthlyData = {};

	chartObject = {
		config:  {
			legend: 'none',
			bar: {
				groupWidth: 30
			},
			tooltip: {isHtml: true},
		},
		columnNames: ['Time', 'Data'],
		hasData: false
	}

	timeChartConfig: any = {...this.chartObject.config};
	timeChartColumnNames = ['Time', 'Data', { role: 'tooltip', type: 'string', p: {html: false}}];

	travelLiftTimeChartColumnNames = [
		'Time',
		this.translate.instant('events.travel_time'), { role: 'tooltip', type: 'string', p: {html: false} }, // travel time
		this.translate.instant('events.lift_time'), { role: 'tooltip', type: 'string', p: {html: false}} // lift time
	];
	travelLiftTimeChartConfig: any = {};

	periodTypes = [
		{label: this.translate.instant('g.monthly'), id: 'monthly'},
		{label: this.translate.instant('g.daily'), id: 'daily'},
	];

	graphTypes = [
		{label: this.translate.instant('g.total'), id: 'total'},
		{label: this.translate.instant('g.daily_avg'), id: 'average'},
	];

	chartsList = [
		{id: 'all', label: this.translate.instant('g.all')},
		{id: 'inuse_hours', label: this.translate.instant('fleet_reports.inuse_hours')},
		{id: 'idle_hours', label: this.translate.instant('fleet_reports.idle_hours')},
		{id: 'inuse_ah', label: this.translate.instant('fleet_reports.inuse_ah')},
		{id: 'inuse_kwh', label: this.translate.instant('fleet_reports.inuse_kwh')},
		{id: 'travel_lift_time', label: this.translate.instant('fleet_reports.travel_lift_time')},
	];

	get printFilters(): { label: string, value: string }[] {
		const filters: { label: string, value: string }[] = [
			{ label: 'g.year', value: this.selectedYear?.toString() },
			{ label: 'perf_analytic.period', value: this.commonService.getOptionLabel(this.periodTypes, this.selectedPeriodType) }
		];

		if (this.selectedPeriodType !== 'daily')
			filters.push({ label: 'g.data', value: this.commonService.getOptionLabel(this.graphTypes, this.selectedGraphTypes) });

		filters.push({ label: 'g.charts', value: this.commonService.getOptionLabel(this.chartsList, this.selectedCharts) });

		return filters;
	}

	constructor(
		private deviceService: DeviceService,
		private translate: TranslateService,
		private siteDashboardService: SiteDashboardService,
		private route: ActivatedRoute,
		private commonService: CommonService,
		private siteService: SitesService
	) {}

	ngOnInit() {
		this.siteId = this.route.parent.snapshot.params.siteId || 0;
		this.getUtilizationSettings();
		if (this.device) {
			if (this.device?.config_info?.installation_date)
				this.deviceInstallationDate = moment.utc(this.device.config_info.installation_date * 1000).startOf('day').toDate();

			this.getTruckUsageChart();
			const {yearList, currentYear} = this.siteDashboardService.prepareYearsList(this.yearList, this.deviceInstallationDate, this.studyInfo?.start_study_time);
			this.yearList = yearList;
			this.selectedYear = currentYear;
		}
	}

	ngOnChanges(changes){
		if((changes.device || changes.studyInfo) && this.device && this.studyInfo)
			this.getTruckUsageChart();
	}

	selectYear() {
		if (this.selectedPeriodType === 'daily')
			return this.getTruckUsageChart();

		this.checkSelectedYearData();
	}

	changePeriodType() {
		if (this.selectedPeriodType === 'daily') {
			this.selectedGraphTypes = 'total';
			this.noData = false;
			return this.getTruckUsageChart();
		}

		return this.prepareMonthlyData(this.monthlyData);
	}

	getTruckUsageChart() {
		const options = {
			is_daily: this.selectedPeriodType === 'daily',
			year: this.selectedYear,
			siteId: this.siteId,
			sct_devices_types: [DeviceFactory.types.iotah],
			studyId: this.studyInfo.id,
		};

		this.deviceService.getUsageChart(this.device?.mac_address, options).subscribe((res: any) => {
			if (this.selectedPeriodType === 'daily')
				this.prepareDailyData(res);
			else {
				this.monthlyData = res;
				this.prepareMonthlyData(this.monthlyData);
			}
		});
	}

	exportDataToCsv() {
		const csvFileHeaderInfo = this.commonService.getCsvFileHeadersInfo({
			customerName: this.currentSite.customer_name,
			siteName: this.currentSite.name,
			deviceSN: this.device.serial_number
		});

		let csvData = [...csvFileHeaderInfo];
		for (let key in this.chartsData) {
			if (this.selectedGraphTypes === 'total' && key.indexOf('total') === -1)
				continue;

			if (this.selectedGraphTypes === 'average' && key.indexOf('average') === -1)
				continue;

			if (this.selectedCharts === 'inuse_hours' &&  key.indexOf('inuse_hours') === -1)
				continue;

			if (this.selectedCharts === 'idle_hours' &&  key.indexOf('idle_hours') === -1)
				continue;

			if (this.selectedCharts === 'inuse_ah' &&  key.indexOf('ah') === -1)
				continue;

			if (this.selectedCharts === 'inuse_kwh' &&  key.indexOf('kwh') === -1)
				continue;

			if (this.selectedCharts === 'travel_lift_time' &&  key.indexOf('lift') === -1)
				continue;

			csvData.push([this.translate.instant('fleet_reports.' + key)]);

			if (key == 'total_travel_lift_time')
				csvData.push([this.translate.instant("g.time"), this.translate.instant('usage_time_chart.travel_time'), this.translate.instant('usage_time_chart.lift_time')]);
			else
				csvData.push([this.translate.instant("g.time"), this.translate.instant("g.data")]);

			let data = this.chartsData[key][this.selectedYear] || [];

			// remove the tooltip column if it exists
			if (key.indexOf('inuse_hours') !== -1 || key.indexOf('idle_hours') !== -1)
				data = data.map(row => row.slice(0, 2));
			if (key.indexOf('travel_lift_time') !== -1)
				data = data.map(row => [row[0], row[1], row[3]]);

			csvData.push(...data);
			csvData.push([]);
		}
		new AngularCsv(csvData, this.translate.instant('fleet_reports.truck_usage_charts'));
	}

	prepareDailyData(data) {
		this.resetCharts();
		const yearId = this.selectedYear;
		this.chartsData.total_inuse_hours[yearId] = [];
		this.chartsData.total_idle_hours[yearId] = [];
		this.chartsData.total_inuse_ah[yearId] = [];
		this.chartsData.total_inuse_kwh[yearId] = [];
		this.chartsData.total_travel_lift_time[yearId] = [];
		this.chartObject.columnNames = [this.translate.instant("g.time"), this.translate.instant("g.data")];

		if (!data || !Object.keys(data).length)
			return this.noData = true;

		for (const day in data) {
			const date = data[day].day;
			const totalInuseHours = data[day].total_in_use_second || 0;
			const totalIdleHours = data[day].total_idle_second || 0;
			const totalInuseAh = data[day].total_discharged_as ? +(data[day].total_discharged_as / 3600).toFixed(2) : 0;
			const totalInuseKwh = data[day].total_ws_used ? +(data[day].total_ws_used / (1000 * 3600)).toFixed(2) : 0;
			const totalTravelTime = data[day].total_travel_second || 0;
			const totalLiftTime =  data[day].total_lift_second || 0;

			this.chartsData.total_inuse_hours[yearId].push([date, (totalInuseHours || 0) / 3600, this.commonService.formatPopupTime(totalInuseHours || 0, date)]);
			this.chartsData.total_idle_hours[yearId].push([date, (totalIdleHours || 0) / 3600, this.commonService.formatPopupTime(totalIdleHours || 0, date)]);
			this.chartsData.total_inuse_ah[yearId].push([date, totalInuseAh || 0]);
			this.chartsData.total_inuse_kwh[yearId].push([date, totalInuseKwh || 0]);
			this.chartsData.total_travel_lift_time[yearId].push([
				date,
				Math.round((totalTravelTime || 0) / 3600), this.commonService.formatPopupTime(totalTravelTime || 0, date), // travel time
				Math.round((totalLiftTime || 0) / 3600), this.commonService.formatPopupTime(totalLiftTime || 0, date) // lift time
			]);
		}

		this.travelLiftTimeChartConfig = {
			...this.timeChartConfig,
			series: {
				0: { targetAxisIndex: 0 },
				1: { targetAxisIndex: 0 } // If you're using multiple series on the same axis
			},
			legend: { position: 'top' },
		}
	}

	prepareMonthlyData(data) {
		this.resetCharts();
		const totalWorkingDays = this.getWorkingDaysCount(this.utilizationSettings.truck_utilization_working_days);

		if (!data || !Object.keys(data).length)
			return this.noData = true;

		for (let year of this.yearList) {
			const yearId = year.id;

			// total
			const totalInuseHours =  data?.data_total_inuse_duration_seconds?.[yearId] || [];
			const totalIdleHours =  data?.data_total_idle_duration_seconds?.[yearId] || [];
			const totalInuseAh = data.data_total_ahr?.[yearId] || [];
			const totalInuseKwh = data.data_total_kwhr?.[yearId] || [];

			const totalTravelTime = data.data_total_travel_time?.[yearId] || [];
			const totalLiftTime = data.data_total_lift_time?.[yearId] || [];

			// average
			const averageInuseHours = data.data_avg_inuse_duration_seconds?.[yearId] || [];
			const averageIdleHours = data.data_avg_idle_duration_seconds?.[yearId] || [];
			const averageInuseAh = data.data_avg_ahr?.[yearId] || [];
			const averageInuseKwh = data.data_avg_kwhr?.[yearId] || [];
			const averageTravelTime = data.data_avg_travel_time?.[yearId] || [];
			const averageLiftTime = data.data_avg_lift_time?.[yearId] || [];

			if (
				!totalInuseHours.length || !totalInuseAh.length || !totalInuseKwh.length || !totalTravelTime.length || !totalLiftTime.length || !totalIdleHours.length ||
				!averageInuseHours.length || !averageInuseAh.length || !averageInuseKwh.length || !averageTravelTime.length || !averageLiftTime.length || !averageIdleHours.length
			) {
				continue;
			}

			this.noData = false;
			// total
			this.chartsData.total_inuse_hours[yearId] = [];
			this.chartsData.total_idle_hours[yearId] = [];
			this.chartsData.total_inuse_ah[yearId] = [];
			this.chartsData.total_inuse_kwh[yearId] = [];
			this.chartsData.total_travel_lift_time[yearId] = [];

			// average
			this.chartsData.average_inuse_hours[yearId] = [];
			this.chartsData.average_idle_hours[yearId] = [];
			this.chartsData.average_inuse_ah[yearId] = [];
			this.chartsData.average_inuse_kwh[yearId] = [];
			this.chartsData.average_travel_lift_time[yearId] = [];

			// total
			for(const period of totalInuseHours) {
				this.chartsData.total_inuse_hours[yearId].push([period[0], Math.round((period[1] / 3600) || 0), this.commonService.formatPopupTime(period[1] || 0, period[0])]); // inuse hours
			}
			for(const period of totalIdleHours) {
				this.chartsData.total_idle_hours[yearId].push([period[0], Math.round((period[1] / 3600) || 0), this.commonService.formatPopupTime(period[1] || 0, period[0])]); // idle hours
			}
			for(const period of totalInuseAh) {
				this.chartsData.total_inuse_ah[yearId].push([period[0], Math.round(period[1] || 0)]);
			}
			for(const period of totalInuseKwh) {
				this.chartsData.total_inuse_kwh[yearId].push([period[0], Math.round(period[1] || 0)]);
			}

			let periodIndex = 0;
			for(const period of totalTravelTime) {
				this.chartsData.total_travel_lift_time[yearId].push([
					period[0],
					Math.round((period[1] || 0) / 3600), this.commonService.formatPopupTime(period[1] || 0, period[0]), // travel time
					Math.round((totalLiftTime[periodIndex][1] || 0) / 3600),  this.commonService.formatPopupTime(totalLiftTime[periodIndex++][1] || 0, period[0])// lift time
				]);
			}

			// average
			for(const period of averageInuseHours) {
				this.chartsData.average_inuse_hours[yearId].push([period[0], Math.round((period[1] / 3600) || 0), this.commonService.formatPopupTime(period[1] || 0, period[0])]);
			}
			for(const period of averageIdleHours) {
				this.chartsData.average_idle_hours[yearId].push([period[0], Math.round((period[1] / 3600) || 0), this.commonService.formatPopupTime(period[1] || 0, period[0])]);
			}
			for(const period of averageInuseAh) {
				this.chartsData.average_inuse_ah[yearId].push([period[0], Math.round(period[1] || 0)/ totalWorkingDays]);
			}
			for(const period of averageInuseKwh) {
				this.chartsData.average_inuse_kwh[yearId].push([period[0], Math.round(period[1] || 0)/ totalWorkingDays]);
			}

			periodIndex = 0;
			for (const period of averageTravelTime) {
				const timestamp = period[0];
				const travelTimeSec = period[1] || 0;
				const liftTimeSec = (totalLiftTime[periodIndex] && totalLiftTime[periodIndex][1]) || 0;

				const travelPerDay = Math.round((travelTimeSec / 3600) / totalWorkingDays);
				const liftPerDay = Math.round((liftTimeSec / 3600) / totalWorkingDays);

				const travelPopup = this.commonService.formatPopupTime(travelTimeSec / totalWorkingDays, timestamp);
				const liftPopup = this.commonService.formatPopupTime(liftTimeSec / totalWorkingDays, timestamp);

				this.chartsData.average_travel_lift_time[yearId].push([
					timestamp,
					travelPerDay, travelPopup,
					liftPerDay, liftPopup
				]);

				periodIndex++;
			}
		}

		this.checkSelectedYearData();
		this.travelLiftTimeChartConfig = {
			...this.timeChartConfig,
			 series: {
				0: { targetAxisIndex: 0 },
				1: { targetAxisIndex: 0 } // If you're using multiple series on the same axis
			},
			legend: { position: 'top' },
		}
	}

	resetCharts() {
		this.chartsData = {
			total_inuse_hours: {},
			total_idle_hours: {},
			total_inuse_ah: {},
			total_inuse_kwh: {},
			total_travel_lift_time: {},
			average_inuse_hours: {},
			average_idle_hours: {},
			average_inuse_ah: {},
			average_inuse_kwh: {},
			average_travel_lift_time: {},
		};
	}

	checkSelectedYearData() {
		this.noData = false;
		if (
			!this.chartsData.total_inuse_hours[this.selectedYear]?.length &&
			!this.chartsData.total_inuse_ah[this.selectedYear]?.length &&
			!this.chartsData.total_inuse_kwh[this.selectedYear]?.length &&
			!this.chartsData.total_travel_lift_time[this.selectedYear]?.length
		) {
			return this.noData = true;
		}
	}

	getUtilizationSettings() {
		this.siteService.getSiteUtilizationSettings(this.siteId).subscribe((res: any) => {
			if (res.length)
				this.utilizationSettings = res[0];
		});
	}

	getWorkingDaysCount(days=[0, 1, 2, 3, 4]) {
		const now = moment();
		const totalDays = now.daysInMonth();
		const year = now.year();
		const month = now.month();
		let total = 0;

		for (let i = 1; i <= totalDays; i++) {
			const day = moment([year, month, i]).isoWeekday() - 1;
			if (days.includes(day)) total++;
		}
		return total;
	}
}
